import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edge, SafeAreaView } from 'react-native-safe-area-context';

import { Button, Modal, ModalDialogue, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderBagLeaveModal = (props: Props) => {
  const { visible, isLeavingGroupOrder, onClose, onConfirm } = props;

  const { formatMessage } = useIntl();

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isLeavingGroupOrder) return;

    onClose();
  }, [isLeavingGroupOrder, onClose]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      visible={visible}
      size="small"
      headline={formatMessage(messages.title)}
      body={formatMessage(messages.body)}
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onRequestClose={handleClose}
    >
      <Modal.Footer withoutTopBorder>
        <SafeAreaView style={styles.footer} edges={SAFE_AREA_EDGES}>
          <View style={styles.ctaContainer}>
            <Button
              size="large"
              palette="secondary"
              disabled={isLeavingGroupOrder}
              onPress={handleClose}
            >
              <FormattedMessage {...messages.stayCta} />
            </Button>
          </View>

          <View style={styles.ctaContainer}>
            <Button
              size="large"
              palette="caution"
              isLoading={isLeavingGroupOrder}
              onPress={onConfirm}
            >
              <FormattedMessage {...messages.leaveOrderCta} />
            </Button>
          </View>
        </SafeAreaView>
      </Modal.Footer>
    </ModalDialogue>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edge[] = Platform.select({
  android: ['bottom'],
  default: [],
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Leave group order confirmation',
    description: 'Bag | Group order | Leave modal | Accessibility label',
  },
  title: {
    defaultMessage: 'Leave group order?',
    description: 'Bag | Group order | Leave modal | Title',
  },
  body: {
    defaultMessage: 'If you leave, your items will be removed and the host will be notified.',
    description: 'Bag | Group order | Leave modal | Body',
  },
  stayCta: {
    defaultMessage: 'Stay',
    description: 'Bag | Group order | Leave modal | Stay CTA',
  },
  leaveOrderCta: {
    defaultMessage: 'Leave order',
    description: 'Bag | Group order | Leave modal | Confirm CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  ctaContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  isLeavingGroupOrder: boolean;
  onClose: () => void;
  onConfirm: () => void;
};
